import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Avatar, Chip, Divider,
  CircularProgress, TextField, InputAdornment,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { Colors } from "../../Theme/Theme";
import { UserTreeApi } from "../../api/requests/network/network";
import { WalletsRequests } from "../../api/requests/Wallets/wallets";
import GetValidatedTokenData from "../../utils/helper";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const findMyNode = (nodes, myCode) => {
  if (!nodes || !Array.isArray(nodes)) return null;
  for (const node of nodes) {
    const code = node.attributes?.shortcode || node.shortcode || "";
    if (code === myCode) return node;
    if (node.children?.length > 0) {
      const found = findMyNode(node.children, myCode);
      if (found) return found;
    }
  }
  return null;
};

const countAll = (arr) => {
  if (!arr || !Array.isArray(arr)) return 0;
  return arr.reduce((sum, n) => sum + 1 + countAll(n.children), 0);
};

export default function TotalTeams() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [walletHistory, setWalletHistory] = useState([]);
  const [myName, setMyName] = useState("You");

  useEffect(() => {
    const tokenData = GetValidatedTokenData();
    const myCode = tokenData?.userPayload?.shortcode || "";
    if (tokenData?.userPayload?.name) setMyName(tokenData.userPayload.name);

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await UserTreeApi.getUserNetwork();
        const allNodes = res?.data || (Array.isArray(res) ? res : []);

        let myChildren = [];

        if (myCode) {
          const myNode = findMyNode(allNodes, myCode);
          if (myNode && myNode.children?.length > 0) {
            myChildren = myNode.children;
          }
        }

        if (myChildren.length === 0 && allNodes.length > 0) {
          allNodes.forEach((rootNode) => {
            if (rootNode.children?.length > 0) {
              myChildren = [...myChildren, ...rootNode.children];
            }
          });
        }

        const members = [];
        const flatten = (arr, level) => {
          if (!arr || !Array.isArray(arr)) return;
          arr.forEach((n) => {
            members.push({
              name: n.name || "Member",
              shortcode: n.attributes?.shortcode || n.shortcode || "---",
              level: level,
              directTeam: n.children?.length || 0,
              totalTeam: countAll(n.children),
            });
            if (n.children?.length > 0) flatten(n.children, level + 1);
          });
        };
        flatten(myChildren, 1);

        setTeamMembers(members);
      } catch (e) {}

      try {
        const walletRes = await WalletsRequests.getWalletHistory();
        setWalletHistory(walletRes?.data || []);
      } catch (e) {}

      setLoading(false);
    };
    fetchData();
  }, []);

  const calcPeriodBusiness = () => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);
    let daily = 0, monthly = 0, yearly = 0, total = 0;
    walletHistory.forEach((t) => {
      if (t.type !== "CREDIT") return;
      const amt = parseFloat(t.amount) || 0;
      total += amt;
      const ts = new Date(t.timestamp);
      if (ts >= todayStart) daily += amt;
      if (ts >= monthStart) monthly += amt;
      if (ts >= yearStart) yearly += amt;
    });
    return { daily, monthly, yearly, total };
  };

  const period = calcPeriodBusiness();

  const filteredMembers = teamMembers.filter((m) => {
    const q = search.toLowerCase();
    return m.name?.toLowerCase().includes(q) || m.shortcode?.toLowerCase().includes(q);
  });

  const directCount = teamMembers.filter((m) => m.level === 1).length;

  const stats = [
    { label: "Your Team", value: teamMembers.length, icon: <GroupIcon sx={{ fontSize: 18 }} />, color: Colors.primary, bg: Colors.blueSoft },
    { label: "Direct Referrals", value: directCount, icon: <PersonIcon sx={{ fontSize: 18 }} />, color: "#059669", bg: Colors.greenSoft },
    { label: "Today's Earnings", value: `Rs. ${period.daily.toLocaleString("en-IN")}`, icon: <CalendarTodayIcon sx={{ fontSize: 18 }} />, color: "#F59E0B", bg: "#FFFBEB" },
    { label: "Monthly Earnings", value: `Rs. ${period.monthly.toLocaleString("en-IN")}`, icon: <TrendingUpIcon sx={{ fontSize: 18 }} />, color: "#8B5CF6", bg: "#F5F3FF" },
  ];

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <GroupIcon sx={{ fontSize: 22, color: Colors.primary }} />
            <Typography sx={{ fontWeight: 500, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              My Team
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px", fontWeight: 400 }}>
            Members under you and your personal business performance.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={36} sx={{ color: Colors.primary }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {stats.map((s, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Box sx={{
                    p: 2, borderRadius: "12px", backgroundColor: Colors.white,
                    border: `1px solid ${Colors.dividerColor}`,
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 500, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        {s.label}
                      </Typography>
                      <Avatar sx={{ width: 28, height: 28, backgroundColor: s.bg, color: s.color }}>
                        {s.icon}
                      </Avatar>
                    </Box>
                    <Typography sx={{ fontWeight: 400, fontSize: "20px", color: "#059669" }}>
                      {s.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Business Bars */}
            <Box sx={{
              backgroundColor: Colors.white, borderRadius: "12px",
              border: `1px solid ${Colors.dividerColor}`, p: 2.5, mb: 3,
            }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 500, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 2 }}>
                Your Personal Earnings
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: "Today", value: period.daily, color: "#059669" },
                  { label: "This Month", value: period.monthly, color: "#2563EB" },
                  { label: "This Year", value: period.yearly, color: "#8B5CF6" },
                  { label: "All Time", value: period.total, color: "#F59E0B" },
                ].map((bar, i) => {
                  const max = Math.max(period.total, 1);
                  return (
                    <Grid item xs={6} sm={3} key={i}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 400, color: Colors.secondaryColor, mb: 0.3 }}>{bar.label}</Typography>
                      <Typography sx={{ fontSize: "16px", fontWeight: 400, color: "#059669", mb: 0.5 }}>
                        Rs. {bar.value.toLocaleString("en-IN")}
                      </Typography>
                      <Box sx={{ height: 6, backgroundColor: "#F1F5F9", borderRadius: "3px", overflow: "hidden" }}>
                        <Box sx={{
                          height: "100%", borderRadius: "3px",
                          backgroundColor: bar.color,
                          width: `${(bar.value / max) * 100}%`,
                          transition: "width 0.6s ease",
                        }} />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Search */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth placeholder="Search your team members..."
                value={search} onChange={(e) => setSearch(e.target.value)} size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: Colors.secondaryColor, fontSize: 20 }} /></InputAdornment>,
                }}
                sx={{
                  maxWidth: 400,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px", backgroundColor: Colors.white,
                    "& fieldset": { borderColor: Colors.dividerColor },
                    "&:hover fieldset": { borderColor: Colors.primary },
                    "&.Mui-focused fieldset": { borderColor: Colors.primary, borderWidth: "2px" },
                  },
                }}
              />
            </Box>

            {/* Members List */}
            <Box sx={{
              backgroundColor: Colors.white, borderRadius: "12px",
              border: `1px solid ${Colors.dividerColor}`, overflow: "hidden",
            }}>
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.primaryTextColor }}>
                  Members Under You ({filteredMembers.length})
                </Typography>
              </Box>
              <Divider sx={{ borderColor: Colors.dividerColor }} />

              {filteredMembers.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <GroupIcon sx={{ fontSize: 36, color: Colors.dividerColor, mb: 1 }} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 400, color: Colors.secondaryColor }}>
                    {search ? "No members match your search" : "No team members under you yet. Start growing your network!"}
                  </Typography>
                </Box>
              ) : (
                filteredMembers.map((member, i) => (
                  <Box key={i}>
                    <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, backgroundColor: Colors.blueSoft, color: Colors.primary, fontSize: 13, fontWeight: 400 }}>
                          {(member.name || "M").charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: Colors.primaryTextColor, lineHeight: 1.3 }}>
                            {member.name}
                          </Typography>
                          <Typography sx={{ fontSize: "10px", fontWeight: 400, color: Colors.secondaryColor }}>
                            ID: {member.shortcode}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Chip
                          label={member.level === 1 ? "Direct" : `Level ${member.level}`}
                          size="small"
                          sx={{
                            height: 20, fontSize: "10px", fontWeight: 500,
                            backgroundColor: member.level === 1 ? Colors.greenSoft : Colors.blueSoft,
                            color: member.level === 1 ? "#059669" : Colors.primary,
                            "& .MuiChip-label": { px: 0.6 },
                          }}
                        />
                        <Box sx={{ textAlign: "right" }}>
                          <Typography sx={{ fontSize: "11px", fontWeight: 400, color: Colors.secondaryColor }}>
                            Sub-team: {member.totalTeam}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    {i < filteredMembers.length - 1 && <Divider sx={{ borderColor: "#F1F5F9" }} />}
                  </Box>
                ))
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
